export const displayValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

export const pad2 = n => String(n).padStart(2, '0');

// The deadline is entered in Jakarta time. Asia/Jakarta has no DST, so a fixed
// offset is exact; it is only used for the client-side "not in the past"
// check, the server converts `{ date, time, tz }` itself.
const JAKARTA_OFFSET = '+07:00';

export const jakartaInputToMs = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return NaN;
  return Date.parse(`${dateStr}T${timeStr}:00${JAKARTA_OFFSET}`);
};

const jakartaNow = () =>
  new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));

export const todayWibDate = () => {
  const now = jakartaNow();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

export const defaultDeadlineTime = () => {
  const now = jakartaNow();
  now.setHours(now.getHours() + 2);
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
};

// The datetime picker works with browser-local Dates. Its wall-clock fields
// (what the user picked or typed) ARE the WIB time, so read/build them with
// local getters: correct whatever the browser timezone is. A wall time the
// browser zone cannot hold (DST gap) or an overflow (31/02) gives null.
export const wibBodyToPickerDate = (dateStr, timeStr) => {
  const [y, m, d] = String(dateStr || '')
    .split('-')
    .map(Number);
  const [h, min] = String(timeStr || '')
    .split(':')
    .map(Number);
  if (![y, m, d, h, min].every(Number.isFinite)) return null;
  const date = new Date(y, m - 1, d, h, min);
  const same =
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d &&
    date.getHours() === h &&
    date.getMinutes() === min;
  return same ? date : null;
};

export const WIB_INPUT_FORMAT = 'dd/MM/yyyy HH:mm';

// Typed `dd/MM/yyyy HH:mm` (1-2 digit parts ok) -> picker Date, or null.
export const parseWibInput = text => {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{1,2})$/.exec(
    String(text || '').trim()
  );
  if (!match) return null;
  const [, d, m, y, h, min] = match;
  return wibBodyToPickerDate(`${y}-${m}-${d}`, `${h}:${min}`);
};

export const pickerDateToWibBody = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return {
    date: `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`,
    time: `${pad2(date.getHours())}:${pad2(date.getMinutes())}`,
    tz: 'Asia/Jakarta',
  };
};

export const remainingSeconds = (deadline, nowMs) => {
  const end = Date.parse(deadline);
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, Math.floor((end - nowMs) / 1000));
};

/** Compact remaining time: always two units (day+h | h+m | m+s). */
export const formatTimerDisplay = seconds => {
  const s = Math.max(0, Number(seconds) || 0);
  const day = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (day > 0) return `${pad2(day)} day ${pad2(h)} h`;
  if (h > 0) return `${pad2(h)} h ${pad2(m)} m`;
  return `${pad2(m)} m ${pad2(sec)} s`;
};

// LPG is priced per metric ton, everything else per barrel.
export const priceUnitSuffix = productName =>
  String(productName || '')
    .toUpperCase()
    .includes('LPG')
    ? '/MT'
    : '/Bbl';

const priceFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

// BE sends prices as numbers (decimal 18,4).
export const formatPrice = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n < 0 ? '-' : ''}$${priceFormat.format(Math.abs(n))}${suffix}`;
};
